/**
 * 🎯 Admin Filter Management System - Type Definitions
 * Complete TypeScript types for filter options management
 */

// ============================================
// Enums
// ============================================

/**
 * Filter status values
 */
export enum FilterStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  SCHEDULED = 'scheduled',
  UNPUBLISHED = 'unpublished'
}

/**
 * Filter category types
 */
export type FilterCategoryType =
  | 'vehicle_type'
  | 'vehicle_brand'
  | 'vehicle_model'
  | 'vehicle_color'
  | 'vehicle_features'
  | 'service_type'
  | 'location'
  | 'price_range'
  | 'availability';

/**
 * Sort order for filter lists
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Filter fields that can be sorted
 */
export type FilterSortField =
  | 'name'
  | 'category'
  | 'status'
  | 'display_order'
  | 'usage_count'
  | 'created_at'
  | 'updated_at';

// ============================================
// Core Interfaces
// ============================================

/**
 * Filter Category entity
 */
export interface FilterCategory {
  id: string;
  categoryName: string;
  categoryKey: string;
  name?: string; // Deprecated: Use categoryName instead
  slug?: string;
  description: string | null;
  icon: string | null;
  display_order: number;
  displayOrder?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Filter Option entity (main model) - matches API contract
 */
export interface FilterOption {
  id: string;
  categoryId: string;
  categoryKey: string;
  categoryName: string;
  optionKey: string;
  optionLabel: string;
  optionDescription: string | null;
  iconName: string | null;
  colorHex: string | null;
  displayOrder: number;
  isGlobal: boolean;
  countryCode: string | null;
  status: FilterStatus;
  publishedAt: string | null;
  unpublishedAt: string | null;
  dbColumnName: string | null;
  dbColumnValue: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  analytics: {
    usageCount: number;
    lastUsedAt: string | null;
  };
  audit: {
    createdBy: string;
    updatedBy: string;
    deletedBy: string | null;
  };
}

/**
 * Pagination metadata - matches API contract
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// ============================================
// Request DTOs
// ============================================

/**
 * DTO for creating a new filter option - matches API contract
 */
export interface CreateFilterOptionDTO {
  categoryId: string;
  optionKey: string;
  optionLabel: string;
  optionDescription?: string | null;
  iconName?: string | null;
  colorHex?: string | null;
  displayOrder?: number;
  isGlobal?: boolean;
  countryCode?: string | null;
  dbColumnName?: string | null;
  dbColumnValue?: string | null;
  publishedAt?: string | null;
  unpublishedAt?: string | null;
}

/**
 * DTO for updating an existing filter option - matches API contract
 * All fields are optional - only send what you want to change
 */
export interface UpdateFilterOptionDTO {
  optionLabel?: string;
  optionDescription?: string | null;
  iconName?: string | null;
  colorHex?: string | null;
  displayOrder?: number;
  publishedAt?: string | null;
  unpublishedAt?: string | null;
  dbColumnName?: string | null;
  dbColumnValue?: string | null;
}

/**
 * Query parameters for listing filter options - matches API contract
 */
export interface ListFilterOptionsParams {
  categoryId?: string;
  category_id?: string;
  status?: FilterStatus;
  includeDeleted?: boolean;
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: FilterSortField;
  sort_order?: SortOrder;
  is_featured?: boolean;
}

/**
 * Query parameters for analytics endpoint - matches API contract
 */
export interface FilterAnalyticsParams {
  categoryId?: string;
  resourceType?: 'vehicle' | 'driver' | 'yacht';
  timeRange?: '7d' | '30d' | '90d' | 'all';
  sortBy?: 'usage' | 'conversion' | 'trending';
}

// ============================================
// Response DTOs
// ============================================

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

/**
 * Single filter option response
 */
export interface FilterOptionResponse {
  success: boolean;
  data: FilterOption;
  message?: string;
}

/**
 * List of filter options response with pagination
 */
export interface FilterOptionsListResponse {
  success: boolean;
  data: {
    filters: FilterOption[];
    pagination: PaginationMeta;
  };
}

/**
 * Filter categories list response
 */
export interface FilterCategoriesResponse {
  success: boolean;
  data: {
    categories: FilterCategory[];
  };
}

// ============================================
// Analytics Types
// ============================================

/**
 * Usage statistics for a single filter
 */
export interface FilterUsageStats {
  filter_id: string;
  filter_name: string;
  category_name: string;
  usage_count: number;
  conversion_rate: number | null;
  trending_score: number | null;
  last_used_at: string | null;
}

/**
 * Category-level analytics summary
 */
export interface CategoryAnalytics {
  category_id: string;
  category_name: string;
  total_filters: number;
  published_filters: number;
  draft_filters: number;
  total_usage: number;
  avg_conversion_rate: number | null;
}

/**
 * Time-series data point for usage trends
 */
export interface UsageTrendDataPoint {
  date: string;
  usage_count: number;
  unique_users: number;
}

/**
 * Complete analytics response
 */
export interface FilterAnalyticsResponse {
  success: boolean;
  data: {
    overview: {
      total_filters: number;
      published_filters: number;
      draft_filters: number;
      scheduled_filters: number;
      total_usage: number;
      avg_conversion_rate: number | null;
    };
    top_filters: FilterUsageStats[];
    category_breakdown: CategoryAnalytics[];
    usage_trends: UsageTrendDataPoint[];
    date_range: {
      from: string;
      to: string;
    };
  };
}

// ============================================
// Error Response
// ============================================

/**
 * Standard error response format
 */
export interface ErrorResponse {
  success: false;
  error: string;
  details?: Record<string, any>;
}

/**
 * API Response wrapper (generic)
 */
export type ApiResponse<T> =
  | { success: true; data: T; message?: string }
  | { success: false; error: string; details?: Record<string, any> };

// ============================================
// Form State Types
// ============================================

/**
 * Form state for create/edit filter modal
 */
export interface FilterFormState {
  category_id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  display_order: number;
  status: FilterStatus;
  metadata: Record<string, any>;
  is_featured: boolean;
  scheduled_publish_at: string | null;
  scheduled_unpublish_at: string | null;
}

/**
 * Validation errors for filter form
 */
export interface FilterFormErrors {
  category_id?: string;
  name?: string;
  slug?: string;
  description?: string;
  icon?: string;
  display_order?: string;
  status?: string;
  scheduled_publish_at?: string;
  scheduled_unpublish_at?: string;
}

// ============================================
// Table Column Types
// ============================================

/**
 * Column configuration for filter management table
 */
export interface FilterTableColumn {
  id: FilterSortField | 'actions';
  label: string;
  sortable: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

/**
 * Filter table row data (extended with UI helpers)
 */
export interface FilterTableRow extends FilterOption {
  categoryName?: string;
  statusColor?: 'default' | 'success' | 'warning' | 'info' | 'error';
}
